import { ReactNode } from 'react'

interface Stage {
  n: number
  title: string
  body: ReactNode
  icon: ReactNode
}

const STAGES: Stage[] = [
  {
    n: 1,
    title: 'AI-assisted intake',
    body: 'An intake built specifically to understand the language of pelvic pain: hormonal cycles, symptom patterns, flare triggers. You complete it on your own time, by voice or by text.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 19a7 7 0 0 0 7-7V8a7 7 0 0 0-14 0v4a7 7 0 0 0 7 7Z" />
        <path d="M12 19v3M8 22h8" />
      </svg>
    ),
  },
  {
    n: 2,
    title: 'Physician sign-off',
    body: 'A licensed OB/GYN works with Pelvia’s intelligence platform to translate your presentation into a structured clinical note. That gives you your official diagnosis.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
        <path d="m9 15 2 2 4-4" />
      </svg>
    ),
  },
  {
    n: 3,
    title: 'You choose what happens next',
    body: 'Every patient-preferred excision surgeon, organized by state, listed clearly. You choose. We send the referral. And if surgery isn’t where you are right now, there’s a real path for that too.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 3v12" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="18" cy="6" r="3" />
        <path d="M18 9a9 9 0 0 1-9 9" />
      </svg>
    ),
  },
  {
    n: 4,
    title: 'Specialist care',
    body: 'You arrive ready, with a complete clinical note, pelvic pain tracking, validated symptom scores, and imaging orders already placed. The surgeon is ready for you. The 7-year wait is over.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M11 2v2M5 8a7 7 0 0 1 14 0v3a4 4 0 0 0 1 2.5 1 1 0 0 1-.8 1.5H4.8a1 1 0 0 1-.8-1.5A4 4 0 0 0 5 11Z" />
        <path d="M9 17v1a3 3 0 0 0 6 0v-1" />
      </svg>
    ),
  },
]

const NON_SURGICAL = ['Pelvic floor PT', 'Hormonal management', 'OBGYN directory']

export default function CareModelDiagram() {
  return (
    <div className="care-diagram reveal">
      <div className="care-diagram-head">
        <span className="care-diagram-badge">
          <span className="care-diagram-badge-pulse" />
          72-hour pathway
        </span>
        <span className="care-companion-tag">Daily pain diary runs alongside every step</span>
      </div>

      <ol className="care-stages">
        {STAGES.map((stage, i) => (
          <li
            key={stage.n}
            className={`care-stage${stage.n === 3 ? ' care-stage-fork' : ''}`}
            style={{ ['--stage-delay' as string]: `${i * 0.08}s` }}
          >
            <div className="care-stage-spine">
              <span className="care-node">
                <span className="care-node-icon">{stage.icon}</span>
                <span className="care-node-num">{stage.n}</span>
              </span>
            </div>

            <div className="care-stage-body">
              <div className="care-stage-card">
                <div className="care-stage-card-head">
                  <span className="care-stage-step">Step {stage.n}</span>
                </div>
                <h3 className="care-stage-title">{stage.title}</h3>
                <p className="care-stage-text">{stage.body}</p>
              </div>

              {stage.n === 3 && (
                <div className="care-fork">
                  <div className="care-fork-path care-fork-surgical">
                    <span className="care-fork-label">Surgical pathway</span>
                    <p>An excision surgeon, chosen by you, with your referral already sent.</p>
                    <span className="care-fork-continues">Continues to specialist care &darr;</span>
                  </div>
                  <div className="care-fork-path care-fork-nonsurgical">
                    <span className="care-fork-label">Non-surgical pathway</span>
                    <p>No one leaves empty-handed. Every path forward is a real path.</p>
                    <div className="care-fork-chips">
                      {NON_SURGICAL.map((c) => (
                        <span key={c} className="care-fork-chip">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>

      <aside className="care-companion">
        <div className="care-companion-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 3v18h18" />
            <path d="m7 14 3-4 3 2 4-6" />
          </svg>
        </div>
        <div className="care-companion-copy">
          <span className="care-companion-label">Running alongside</span>
          <strong>Daily tracking &amp; pain diary</strong>
          <p>
            Continuous symptom tracking feeds richer evidence into your note and your specialist
            visit. The longer you track, the stronger your case.
          </p>
          <a href="#app-waitlist" className="care-companion-link">
            Get on the list
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </a>
        </div>
      </aside>
    </div>
  )
}
